import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Tag,
  Table,
  Tabs,
  Input,
  Select,
  Button,
  Spin,
  Empty,
  message,
} from "antd";
import {
  DownloadOutlined,
  FileTextOutlined,
  CarOutlined,
  TeamOutlined,
  ExperimentOutlined,
  WarningOutlined,
  SearchOutlined,
  CalendarOutlined,
  DashboardOutlined,
  RiseOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  ZAxis,
} from "recharts";
import dayjs from "dayjs";
import debounce from "lodash.debounce";
import { useDateRange } from "../layout/AppLayout";
import axios from "../../services/axiosInstance";
import {
  WEIGHTMENT_OVERVIEW,
  WEIGHTMENT_TREND,
  WEIGHTMENT_BY_MATERIAL,
  WEIGHTMENT_BY_PARTY,
  WEIGHTMENT_BY_VEHICLE,
  WEIGHTMENT_BY_LOGISTICS,
  WEIGHTMENT_BY_STATE,
  WEIGHTMENT_SLIPS,
  WEIGHTMENT_HOURLY,
  WEIGHTMENT_SCATTER,
  WEIGHTMENT_TARE_CONSISTENCY,
  WEIGHTMENT_FILTERS,
  WEIGHTMENT_RAKE_REPORT,
} from "../../services/apiEndPoints";
import type {
  WeightmentKPI,
  WeightTrendItem,
  MaterialBreakdown,
  PartyBreakdown,
  VehicleAnalytics,
  LogisticsBreakdown,
  StateBreakdown,
  HourlyPattern,
  ScatterPoint,
  TareAnomaly,
  IWeightmentSlip,
  WeightmentFilters,
} from "../types/weightment.types";
import type { ColumnsType } from "antd/es/table";

const { Title, Text } = Typography;

/* ─── Constants ──────────────────────────────────────────── */

const MATERIAL_COLORS = ["#1677ff", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#94a3b8", "#ef4444", "#14b8a6", "#a855f7"];
const STATE_COLORS = ["#1677ff", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#94a3b8", "#06b6d4", "#ef4444"];

/* ─── Helpers ────────────────────────────────────────────── */

const formatWeight = (kg: number) => {
  if (kg >= 1_000_000) return `${(kg / 1_000_000).toFixed(2)}M kg`;
  if (kg >= 1_000) return `${(kg / 1_000).toFixed(1)}K kg`;
  return `${kg} kg`;
};

const formatNumber = (n: number) => n.toLocaleString("en-IN");

const formatCompact = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const getDateParams = (dateRange: [dayjs.Dayjs, dayjs.Dayjs]) => ({
  fromDate: dateRange[0].format("YYYY-MM-DD"),
  toDate: dateRange[1].format("YYYY-MM-DD"),
});

/* ─── Component ──────────────────────────────────────────── */

const WeightmentComponent = () => {
  const { dateRange } = useDateRange();

  // Data states
  const [kpi, setKpi] = useState<WeightmentKPI | null>(null);
  const [trend, setTrend] = useState<WeightTrendItem[]>([]);
  const [trendGroupedBy, setTrendGroupedBy] = useState("day");
  const [materials, setMaterials] = useState<MaterialBreakdown[]>([]);
  const [parties, setParties] = useState<PartyBreakdown[]>([]);
  const [vehicles, setVehicles] = useState<VehicleAnalytics[]>([]);
  const [vehicleTotal, setVehicleTotal] = useState(0);
  const [logistics, setLogistics] = useState<LogisticsBreakdown[]>([]);
  const [states, setStates] = useState<StateBreakdown[]>([]);
  const [hourly, setHourly] = useState<HourlyPattern[]>([]);
  const [scatter, setScatter] = useState<ScatterPoint[]>([]);
  const [tareAnomalies, setTareAnomalies] = useState<TareAnomaly[]>([]);
  const [slips, setSlips] = useState<IWeightmentSlip[]>([]);
  const [slipTotal, setSlipTotal] = useState(0);
  const [filters, setFilters] = useState<WeightmentFilters | null>(null);

  // UI states
  const [loading, setLoading] = useState(true);
  const [slipPage, setSlipPage] = useState(1);
  const [slipPageSize, setSlipPageSize] = useState(50);
  const [slipSearch, setSlipSearch] = useState("");
  const [slipMaterial, setSlipMaterial] = useState<string | undefined>();
  const [slipCity, setSlipCity] = useState<string | undefined>();
  const [slipState, setSlipState] = useState<string | undefined>();
  const [slipSortBy, setSlipSortBy] = useState("slipDate");
  const [slipOrder, setSlipOrder] = useState<"asc" | "desc">("desc");
  const [vehiclePage, setVehiclePage] = useState(1);
  const [vehiclePageSize, setVehiclePageSize] = useState(20);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [vehicleSortBy, setVehicleSortBy] = useState("totalNetWeight");
  const [vehicleOrder, setVehicleOrder] = useState<"asc" | "desc">("desc");
  const [slipsLoading, setSlipsLoading] = useState(false);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  const dateParams = useMemo(() => getDateParams(dateRange), [dateRange]);

  // ─── Fetch dashboard data (all charts) ──────────────
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        kpiRes,
        trendRes,
        materialRes,
        partyRes,
        logisticsRes,
        stateRes,
        hourlyRes,
        scatterRes,
        tareRes,
        filterRes,
      ] = await Promise.all([
        axios.get(WEIGHTMENT_OVERVIEW, { params: dateParams }),
        axios.get(WEIGHTMENT_TREND, { params: dateParams }),
        axios.get(WEIGHTMENT_BY_MATERIAL, { params: dateParams }),
        axios.get(WEIGHTMENT_BY_PARTY, { params: { ...dateParams, limit: "10" } }),
        axios.get(WEIGHTMENT_BY_LOGISTICS, { params: dateParams }),
        axios.get(WEIGHTMENT_BY_STATE, { params: dateParams }),
        axios.get(WEIGHTMENT_HOURLY, { params: dateParams }),
        axios.get(WEIGHTMENT_SCATTER, { params: dateParams }),
        axios.get(WEIGHTMENT_TARE_CONSISTENCY, { params: { ...dateParams, threshold: "0.15" } }),
        axios.get(WEIGHTMENT_FILTERS),
      ]);

      setKpi(kpiRes.data?.data ?? null);
      setTrend(trendRes.data?.results ?? []);
      setTrendGroupedBy(trendRes.data?.groupedBy ?? "day");
      setMaterials(materialRes.data?.results ?? []);
      setParties(partyRes.data?.results ?? []);
      setLogistics(logisticsRes.data?.results ?? []);
      setStates(stateRes.data?.results ?? []);
      setHourly(hourlyRes.data?.results ?? []);
      setScatter(scatterRes.data?.results ?? []);
      const tareResults = tareRes.data?.results ?? [];
      setTareAnomalies(tareResults.filter((t: TareAnomaly) => t.flagged));
      setFilters(filterRes.data?.data ?? null);
    } finally {
      setLoading(false);
    }
  }, [dateParams]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // ─── Fetch slips (paginated) ────────────────────────
  const fetchSlips = useCallback(async () => {
    setSlipsLoading(true);
    try {
      const res = await axios.get(WEIGHTMENT_SLIPS, {
        params: {
          ...dateParams,
          page: String(slipPage),
          limit: String(slipPageSize),
          sortBy: slipSortBy,
          order: slipOrder,
          ...(slipSearch && { search: slipSearch }),
          ...(slipMaterial && { material: slipMaterial }),
          ...(slipCity && { city: slipCity }),
          ...(slipState && { state: slipState }),
        },
      });
      setSlips(res.data?.results ?? []);
      setSlipTotal(res.data?.total ?? 0);
    } finally {
      setSlipsLoading(false);
    }
  }, [dateParams, slipPage, slipPageSize, slipSortBy, slipOrder, slipSearch, slipMaterial, slipCity, slipState]);

  useEffect(() => {
    fetchSlips();
  }, [fetchSlips]);

  // ─── Fetch vehicles (paginated) ─────────────────────
  const fetchVehicles = useCallback(async () => {
    setVehiclesLoading(true);
    try {
      const res = await axios.get(WEIGHTMENT_BY_VEHICLE, {
        params: {
          ...dateParams,
          page: String(vehiclePage),
          limit: String(vehiclePageSize),
          sortBy: vehicleSortBy,
          order: vehicleOrder,
          ...(vehicleSearch && { search: vehicleSearch }),
        },
      });
      setVehicles(res.data?.results ?? []);
      setVehicleTotal(res.data?.total ?? 0);
    } finally {
      setVehiclesLoading(false);
    }
  }, [dateParams, vehiclePage, vehiclePageSize, vehicleSortBy, vehicleOrder, vehicleSearch]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // ─── Debounced search handlers ──────────────────────
  const debouncedSlipSearch = useMemo(
    () => debounce((val: string) => { setSlipSearch(val); setSlipPage(1); }, 400),
    []
  );
  const debouncedVehicleSearch = useMemo(
    () => debounce((val: string) => { setVehicleSearch(val); setVehiclePage(1); }, 400),
    []
  );

  // ─── Rake report download ──────────────────────────
  const downloadRakeReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const res = await axios.get(WEIGHTMENT_RAKE_REPORT, {
        params: dateParams,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rake_Report_${dateParams.fromDate}_to_${dateParams.toDate}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error("Failed to download rake report");
    } finally {
      setReportLoading(false);
    }
  }, [dateParams]);

  // ─── KPI card config ───────────────────────────────
  const kpiCards = useMemo(() => {
    if (!kpi) return [];
    return [
      { label: "Total Slips", value: formatCompact(kpi.totalSlips), icon: <FileTextOutlined />, color: "#1677ff", gradient: "linear-gradient(135deg, #1677ff, #4096ff)" },
      { label: "Total Net Weight", value: formatWeight(kpi.totalNetWeight), icon: <DashboardOutlined />, color: "#10b981", gradient: "linear-gradient(135deg, #10b981, #34d399)" },
      { label: "Avg Net Weight", value: formatWeight(Math.round(kpi.avgNetWeight)), icon: <BarChartOutlined />, color: "#8b5cf6", gradient: "linear-gradient(135deg, #8b5cf6, #a78bfa)" },
      { label: "Unique Vehicles", value: formatCompact(kpi.uniqueVehicles), icon: <CarOutlined />, color: "#f59e0b", gradient: "linear-gradient(135deg, #f59e0b, #fbbf24)" },
      { label: "Unique Parties", value: formatCompact(kpi.uniqueParties), icon: <TeamOutlined />, color: "#ec4899", gradient: "linear-gradient(135deg, #ec4899, #f472b6)" },
      { label: "Today's Slips", value: formatCompact(kpi.todaySlips), icon: <RiseOutlined />, color: "#06b6d4", gradient: "linear-gradient(135deg, #06b6d4, #22d3ee)" },
    ];
  }, [kpi]);

  // ─── Derived chart data ─────────────────────────────
  const maxPartyWeight = parties.length > 0 ? parties[0].totalNetWeight : 1;

  const materialWithColors = useMemo(
    () => materials.map((m, i) => ({ ...m, color: MATERIAL_COLORS[i % MATERIAL_COLORS.length] })),
    [materials]
  );

  const stateWithColors = useMemo(
    () => states.map((s, i) => ({ ...s, color: STATE_COLORS[i % STATE_COLORS.length] })),
    [states]
  );

  // ─── Table columns ─────────────────────────────────
  const slipColumns: ColumnsType<IWeightmentSlip> = [
    { title: "S.No", dataIndex: "serialNo", key: "serialNo", width: 80, sorter: true },
    { title: "Vehicle", dataIndex: "vehicleNumber", key: "vehicleNumber", width: 150, render: (v: string) => <Text strong style={{ fontSize: 13, color: "#1677ff" }}>{v}</Text> },
    { title: "Date", dataIndex: "slipDate", key: "slipDate", width: 110, sorter: true, render: (v: string) => dayjs(v).format("DD MMM YYYY") },
    { title: "Material", dataIndex: "material", key: "material", width: 110, render: (v: string) => <Tag style={{ margin: 0, borderRadius: 6 }}>{v}</Tag> },
    { title: "Party", dataIndex: "party", key: "party", width: 160, ellipsis: true },
    { title: "Gross (kg)", dataIndex: "grossWeight", key: "grossWeight", width: 110, align: "right", sorter: true, render: (v: number) => formatNumber(v) },
    { title: "Tare (kg)", dataIndex: "tareWeight", key: "tareWeight", width: 100, align: "right", sorter: true, render: (v: number) => formatNumber(v) },
    { title: "Net (kg)", dataIndex: "netWeight", key: "netWeight", width: 110, align: "right", sorter: true, render: (v: number) => <Text strong style={{ color: "#10b981" }}>{formatNumber(v)}</Text> },

    { title: "Location", dataIndex: "location", key: "location", width: 100 },
    { title: "City", dataIndex: "city", key: "city", width: 100 },
  ];

  const vehicleColumns: ColumnsType<VehicleAnalytics> = [
    { title: "Vehicle Number", dataIndex: "vehicleNumber", key: "vehicleNumber", render: (v: string) => <Text strong style={{ fontSize: 13, color: "#1677ff" }}>{v}</Text> },
    { title: "Trips", dataIndex: "tripCount", key: "tripCount", sorter: true, align: "right" },
    { title: "Total Net (kg)", dataIndex: "totalNetWeight", key: "totalNetWeight", sorter: true, align: "right", render: (v: number) => formatNumber(v) },
    { title: "Avg Net (kg)", dataIndex: "avgNetWeight", key: "avgNetWeight", sorter: true, align: "right", render: (v: number) => formatNumber(Math.round(v)) },
    { title: "First Seen", dataIndex: "firstSeen", key: "firstSeen", render: (v: string) => dayjs(v).format("DD MMM YYYY") },
    { title: "Last Seen", dataIndex: "lastSeen", key: "lastSeen", render: (v: string) => dayjs(v).format("DD MMM YYYY") },
    { title: "Materials", dataIndex: "materials", key: "materials", render: (v: string[]) => v?.slice(0, 2).map((m) => <Tag key={m} style={{ margin: "0 2px", borderRadius: 6, fontSize: 11 }}>{m}</Tag>) },
  ];

  const handleSlipTableChange = useCallback((pagination: any, _filters: any, sorter: any) => {
    setSlipPage(pagination.current);
    setSlipPageSize(pagination.pageSize);
    if (sorter.field) {
      setSlipSortBy(sorter.field);
      setSlipOrder(sorter.order === "ascend" ? "asc" : "desc");
    }
  }, []);

  const handleVehicleTableChange = useCallback((pagination: any, _filters: any, sorter: any) => {
    setVehiclePage(pagination.current);
    setVehiclePageSize(pagination.pageSize);
    if (sorter.field) {
      const fieldMap: Record<string, string> = { tripCount: "trips", totalNetWeight: "totalNetWeight", avgNetWeight: "avgNetWeight" };
      setVehicleSortBy(fieldMap[sorter.field] || sorter.field);
      setVehicleOrder(sorter.order === "ascend" ? "asc" : "desc");
    }
  }, []);

  // ─── Trend period label ─────────────────────────────
  const trendPeriodLabel = trendGroupedBy === "day" ? "Daily" : trendGroupedBy === "week" ? "Weekly" : "Monthly";

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 32 }}>
      {/* ─── Page Header ──────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
          borderRadius: 16,
          padding: "28px 32px",
          marginBottom: 24,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -40, right: -20, width: 200, height: 200, borderRadius: "50%", background: "rgba(22,119,255,0.08)" }} />
        <div style={{ position: "absolute", bottom: -30, right: 100, width: 120, height: 120, borderRadius: "50%", background: "rgba(139,92,246,0.06)" }} />
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
              Weightment Dashboard
            </Title>
            <Text style={{ color: "#94a3b8", fontSize: 14, display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
              <ExperimentOutlined /> Weighbridge analytics & slip management
            </Text>
          </Col>
          <Col>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Tag style={{ margin: 0, borderRadius: 8, fontWeight: 500, fontSize: 12, padding: "4px 12px" }} color="default">
                <CalendarOutlined style={{ marginRight: 4 }} />
                {dateRange[0].format("DD MMM")} — {dateRange[1].format("DD MMM YYYY")}
              </Tag>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={reportLoading}
                onClick={downloadRakeReport}
                style={{ borderRadius: 8, fontWeight: 600 }}
              >
                Rake Report
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {/* ─── KPI Cards ─────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {kpiCards.map((card) => (
          <Col xs={24} sm={12} lg={4} key={card.label}>
            <Card className="stat-card" style={{ borderRadius: 14, border: "1px solid #e2e8f0" }} styles={{ body: { padding: "18px 20px" } }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <Text style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 4 }}>{card.label}</Text>
                  <Text style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>{card.value}</Text>
                </div>
                <div
                  style={{
                    width: 42, height: 42, borderRadius: 12, background: card.gradient,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 19, color: "#fff", boxShadow: `0 4px 12px ${card.color}30`,
                  }}
                >
                  {card.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ─── Section A: Weight Trend + Material Donut ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} xl={16}>
          <Card className="content-card" styles={{ body: { padding: "20px 24px" } }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <Text strong style={{ fontSize: 16 }}>Weight Trend</Text>
                <Text style={{ color: "#94a3b8", fontSize: 12, display: "block", marginTop: 2 }}>
                  Gross, tare & net weight over time
                </Text>
              </div>
              <Tag style={{ margin: 0, borderRadius: 6 }} color="default">{trendPeriodLabel}</Tag>
            </div>
            {trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="wGrossGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1677ff" stopOpacity={0.1} />
                      <stop offset="95%" stopColor="#1677ff" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="wNetGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.1} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="wTareGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.1} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => dayjs(v).format("DD MMM")}
                  />
                  <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                  <Tooltip
                    contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                    labelFormatter={(v) => dayjs(v).format("DD MMM YYYY")}
                    formatter={(value: any, name: any) => [formatNumber(value) + " kg", name]}
                  />
                  <Area type="monotone" dataKey="totalGrossWeight" stroke="#1677ff" strokeWidth={2.5} fill="url(#wGrossGrad)" name="Gross Weight" />
                  <Area type="monotone" dataKey="totalNetWeight" stroke="#10b981" strokeWidth={2.5} fill="url(#wNetGrad)" name="Net Weight" />
                  <Area type="monotone" dataKey="totalTareWeight" stroke="#f59e0b" strokeWidth={2} fill="url(#wTareGrad)" name="Tare Weight" strokeDasharray="5 5" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No trend data" style={{ padding: 60 }} />
            )}
          </Card>
        </Col>

        {/* Material Donut */}
        <Col xs={24} xl={8}>
          <Card className="content-card" style={{ height: "100%" }} styles={{ body: { padding: "20px 24px", height: "100%", display: "flex", flexDirection: "column" } }}>
            <Text strong style={{ fontSize: 16, display: "block" }}>Material Breakdown</Text>
            <Text style={{ color: "#94a3b8", fontSize: 12, display: "block", marginTop: 2, marginBottom: 8 }}>
              Net weight share by material
            </Text>
            {materialWithColors.length > 0 ? (
              <>
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={materialWithColors} dataKey="totalNetWeight" nameKey="material" cx="50%" cy="50%" outerRadius={85} innerRadius={55} paddingAngle={3} strokeWidth={0}>
                        {materialWithColors.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 10, fontSize: 13 }} formatter={(value: any) => [formatWeight(value), "Net Weight"]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                  {materialWithColors.slice(0, 5).map((item) => (
                    <div key={item.material} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: item.color }} />
                        <Text style={{ fontSize: 13 }}>{item.material}</Text>
                      </div>
                      <Text strong style={{ fontSize: 13 }}>{formatWeight(item.totalNetWeight)}</Text>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Empty description="No material data" style={{ padding: 40 }} />
            )}
          </Card>
        </Col>
      </Row>

      {/* ─── Section B: Party Leaderboard + Hourly Pattern + State ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* Party Leaderboard */}
        <Col xs={24} lg={8}>
          <Card className="content-card" style={{ height: "100%" }} styles={{ body: { padding: "20px 24px" } }}>
            <Text strong style={{ fontSize: 16, display: "block" }}>Top Parties</Text>
            <Text style={{ color: "#94a3b8", fontSize: 12, display: "block", marginTop: 2, marginBottom: 16 }}>
              By total net weight
            </Text>
            {parties.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {parties.slice(0, 7).map((p, i) => (
                  <div key={p.party}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div
                          style={{
                            width: 22, height: 22, borderRadius: 6,
                            background: i < 3 ? "#1677ff" : "#f1f5f9",
                            color: i < 3 ? "#fff" : "#64748b",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700,
                          }}
                        >
                          {i + 1}
                        </div>
                        <Text style={{ fontSize: 13, fontWeight: 500 }}>{p.party}</Text>
                      </div>
                      <Text style={{ fontSize: 12, color: "#64748b" }}>{formatWeight(p.totalNetWeight)}</Text>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: "#f1f5f9", overflow: "hidden" }}>
                      <div
                        style={{
                          height: "100%",
                          width: `${(p.totalNetWeight / maxPartyWeight) * 100}%`,
                          borderRadius: 3,
                          background: i < 3 ? "linear-gradient(90deg, #1677ff, #4096ff)" : "#cbd5e1",
                          transition: "width 0.5s ease",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty description="No party data" style={{ padding: 40 }} />
            )}
          </Card>
        </Col>

        {/* Hourly Pattern */}
        <Col xs={24} lg={8}>
          <Card className="content-card" style={{ height: "100%" }} styles={{ body: { padding: "20px 24px" } }}>
            <Text strong style={{ fontSize: 16, display: "block" }}>Hourly Pattern</Text>
            <Text style={{ color: "#94a3b8", fontSize: 12, display: "block", marginTop: 2, marginBottom: 16 }}>
              Slip count by hour of day
            </Text>
            {hourly.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={hourly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(h) => `${h}:00`} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 10, fontSize: 13 }}
                    labelFormatter={(h) => `${h}:00 - ${h}:59`}
                    formatter={(v: any, name: any) => [formatNumber(v), name === "slipCount" ? "Slips" : "Net Weight (kg)"]}
                  />
                  <Bar dataKey="slipCount" name="slipCount" radius={[4, 4, 0, 0]}>
                    {hourly.map((entry, i) => (
                      <Cell key={i} fill={entry.slipCount >= 65 ? "#1677ff" : entry.slipCount >= 40 ? "#4096ff" : "#93c5fd"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No hourly data" style={{ padding: 40 }} />
            )}
          </Card>
        </Col>

        {/* State Breakdown */}
        <Col xs={24} lg={8}>
          <Card className="content-card" style={{ height: "100%" }} styles={{ body: { padding: "20px 24px" } }}>
            <Text strong style={{ fontSize: 16, display: "block" }}>State Breakdown</Text>
            <Text style={{ color: "#94a3b8", fontSize: 12, display: "block", marginTop: 2, marginBottom: 8 }}>
              Slip distribution by state
            </Text>
            {stateWithColors.length > 0 ? (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={stateWithColors} dataKey="slipCount" nameKey="state" cx="50%" cy="50%" outerRadius={75} innerRadius={45} paddingAngle={2} strokeWidth={0}>
                        {stateWithColors.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 10, fontSize: 13 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                  {stateWithColors.map((item) => (
                    <div key={item.state} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: item.color }} />
                        <Text style={{ fontSize: 12 }}>{item.state}</Text>
                      </div>
                      <Text strong style={{ fontSize: 12 }}>{formatNumber(item.slipCount)}</Text>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Empty description="No state data" style={{ padding: 40 }} />
            )}
          </Card>
        </Col>
      </Row>

      {/* ─── Section C: Scatter Plot + Logistics ────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={14}>
          <Card className="content-card" styles={{ body: { padding: "20px 24px" } }}>
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ fontSize: 16, display: "block" }}>Gross vs Net Weight</Text>
              <Text style={{ color: "#94a3b8", fontSize: 12, display: "block", marginTop: 2 }}>
                Scatter plot to detect anomalies — outliers may indicate weighbridge issues
              </Text>
            </div>
            {scatter.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    type="number" dataKey="grossWeight" name="Gross"
                    tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`}
                    label={{ value: "Gross Weight (kg)", position: "insideBottom", offset: -5, style: { fontSize: 11, fill: "#94a3b8" } }}
                  />
                  <YAxis
                    type="number" dataKey="netWeight" name="Net"
                    tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`}
                    label={{ value: "Net Weight (kg)", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#94a3b8" } }}
                  />
                  <ZAxis range={[60, 60]} />
                  <Tooltip
                    contentStyle={{ borderRadius: 10, fontSize: 13 }}
                    formatter={(value: any) => [formatNumber(value) + " kg"]}
                    labelFormatter={() => ""}
                    cursor={{ strokeDasharray: "3 3" }}
                  />
                  <Scatter name="Vehicles" data={scatter} fill="#1677ff">
                    {scatter.map((entry, i) => (
                      <Cell key={i} fill={entry.tareWeight > 10000 ? "#ef4444" : entry.tareWeight > 7000 ? "#f59e0b" : "#1677ff"} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No scatter data" style={{ padding: 60 }} />
            )}
          </Card>
        </Col>

        {/* Logistics Leaderboard */}
        <Col xs={24} lg={10}>
          <Card className="content-card" style={{ height: "100%" }} styles={{ body: { padding: "20px 24px" } }}>
            <Text strong style={{ fontSize: 16, display: "block" }}>Logistics Performance</Text>
            <Text style={{ color: "#94a3b8", fontSize: 12, display: "block", marginTop: 2, marginBottom: 16 }}>
              Ranked by volume handled
            </Text>
            {logistics.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {logistics.map((l, i) => (
                  <div
                    key={l.logisticsName}
                    style={{
                      padding: "14px 16px",
                      background: i === 0 ? "linear-gradient(135deg, rgba(22,119,255,0.06), rgba(22,119,255,0.02))" : "#f8fafc",
                      borderRadius: 10,
                      border: `1px solid ${i === 0 ? "rgba(22,119,255,0.15)" : "#f1f5f9"}`,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                          style={{
                            width: 28, height: 28, borderRadius: 8,
                            background: i === 0 ? "#1677ff" : "#e2e8f0",
                            color: i === 0 ? "#fff" : "#64748b",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 12, fontWeight: 700,
                          }}
                        >
                          {i + 1}
                        </div>
                        <div>
                          <Text style={{ fontSize: 13, fontWeight: 600, display: "block", lineHeight: 1.3 }}>{l.logisticsName}</Text>
                          <Text style={{ fontSize: 11, color: "#94a3b8" }}>
                            {l.slipCount} slips · {l.uniqueVehicles} vehicles
                          </Text>
                        </div>
                      </div>
                      <Text strong style={{ fontSize: 14, color: i === 0 ? "#1677ff" : "#0f172a" }}>
                        {formatWeight(l.totalNetWeight)}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty description="No logistics data" style={{ padding: 40 }} />
            )}
          </Card>
        </Col>
      </Row>

      {/* ─── Section D: Anomaly Alerts ─────────────── */}
      {tareAnomalies.length > 0 && (
        <Card
          className="content-card"
          style={{ marginBottom: 24, borderColor: "rgba(239,68,68,0.2)" }}
          styles={{ body: { padding: "20px 24px" } }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div
              style={{
                width: 36, height: 36, borderRadius: 10,
                background: "rgba(239,68,68,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <WarningOutlined style={{ color: "#ef4444", fontSize: 18 }} />
            </div>
            <div>
              <Text strong style={{ fontSize: 16, display: "block" }}>Tare Weight Anomalies</Text>
              <Text style={{ color: "#94a3b8", fontSize: 12 }}>
                Vehicles with inconsistent tare weights — potential overloading or weighbridge manipulation
              </Text>
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {tareAnomalies.map((a) => (
              <div
                key={a.vehicleNumber}
                style={{
                  flex: "1 1 280px",
                  padding: "16px 18px",
                  background: "rgba(239,68,68,0.03)",
                  borderRadius: 10,
                  border: "1px solid rgba(239,68,68,0.12)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <Text strong style={{ fontSize: 14, color: "#1677ff" }}>{a.vehicleNumber}</Text>
                  <Tag color="error" style={{ margin: 0, borderRadius: 6, fontWeight: 600 }}>
                    <WarningOutlined style={{ marginRight: 4 }} />
                    {(a.tareVariance * 100).toFixed(1)}% variance
                  </Tag>
                </div>
                <Row gutter={16}>
                  <Col span={8}>
                    <Text style={{ fontSize: 11, color: "#94a3b8", display: "block" }}>Min Tare</Text>
                    <Text style={{ fontSize: 13, fontWeight: 600 }}>{formatNumber(a.minTare)} kg</Text>
                  </Col>
                  <Col span={8}>
                    <Text style={{ fontSize: 11, color: "#94a3b8", display: "block" }}>Max Tare</Text>
                    <Text style={{ fontSize: 13, fontWeight: 600 }}>{formatNumber(a.maxTare)} kg</Text>
                  </Col>
                  <Col span={8}>
                    <Text style={{ fontSize: 11, color: "#94a3b8", display: "block" }}>Trips</Text>
                    <Text style={{ fontSize: 13, fontWeight: 600 }}>{a.tripCount}</Text>
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ─── Section E: Data Tables ────────────────── */}
      <Card className="content-card" styles={{ body: { padding: 0 } }}>
        <Tabs
          defaultActiveKey="slips"
          style={{ marginBottom: 0 }}
          tabBarStyle={{ padding: "0 24px", marginBottom: 0 }}
          items={[
            {
              key: "slips",
              label: (
                <span>
                  Slip Log
                  <span className="tab-badge" style={{ background: "#eff6ff", color: "#1677ff" }}>{slipTotal}</span>
                </span>
              ),
              children: (
                <div>
                  <div className="table-toolbar">
                    <Input
                      placeholder="Search vehicle, material, party, wagon..."
                      prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                      style={{ width: 320, borderRadius: 8 }}
                      allowClear
                      onChange={(e) => debouncedSlipSearch(e.target.value)}
                    />
                    <div style={{ display: "flex", gap: 8 }}>
                      <Select
                        placeholder="Material"
                        style={{ width: 130 }}
                        allowClear
                        value={slipMaterial}
                        onChange={(v) => { setSlipMaterial(v); setSlipPage(1); }}
                        options={filters?.materials.map((m) => ({ label: m, value: m })) ?? []}
                      />
                      <Select
                        placeholder="City"
                        style={{ width: 120 }}
                        allowClear
                        value={slipCity}
                        onChange={(v) => { setSlipCity(v); setSlipPage(1); }}
                        options={filters?.cities.map((c) => ({ label: c, value: c })) ?? []}
                      />
                      <Select
                        placeholder="State"
                        style={{ width: 140 }}
                        allowClear
                        value={slipState}
                        onChange={(v) => { setSlipState(v); setSlipPage(1); }}
                        options={filters?.states.map((s) => ({ label: s, value: s })) ?? []}
                      />
                    </div>
                  </div>
                  <Table
                    columns={slipColumns}
                    dataSource={slips}
                    rowKey="weightmentSlipId"
                    size="small"
                    scroll={{ x: 1200 }}
                    loading={slipsLoading}
                    onChange={handleSlipTableChange}
                    pagination={{
                      current: slipPage,
                      pageSize: slipPageSize,
                      total: slipTotal,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100"],
                      showTotal: (total) => `${total} slips`,
                    }}
                  />
                </div>
              ),
            },
            {
              key: "vehicles",
              label: (
                <span>
                  Vehicle Summary
                  <span className="tab-badge" style={{ background: "#f0fdf4", color: "#10b981" }}>{vehicleTotal}</span>
                </span>
              ),
              children: (
                <div>
                  <div className="table-toolbar">
                    <Input
                      placeholder="Search vehicle number..."
                      prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                      style={{ width: 280, borderRadius: 8 }}
                      allowClear
                      onChange={(e) => debouncedVehicleSearch(e.target.value)}
                    />
                  </div>
                  <Table
                    columns={vehicleColumns}
                    dataSource={vehicles}
                    rowKey="vehicleNumber"
                    size="small"
                    loading={vehiclesLoading}
                    onChange={handleVehicleTableChange}
                    pagination={{
                      current: vehiclePage,
                      pageSize: vehiclePageSize,
                      total: vehicleTotal,
                      showSizeChanger: true,
                      showTotal: (total) => `${total} vehicles`,
                    }}
                  />
                </div>
              ),
            },
            {
              key: "parties",
              label: (
                <span>
                  Party Summary
                  <span className="tab-badge" style={{ background: "#fdf4ff", color: "#ec4899" }}>{parties.length}</span>
                </span>
              ),
              children: (
                <div>
                  <Table
                    columns={[
                      { title: "Party", dataIndex: "party", key: "party", render: (v: string) => <Text strong style={{ fontSize: 13 }}>{v}</Text> },
                      { title: "Total Net (kg)", dataIndex: "totalNetWeight", key: "totalNetWeight", sorter: (a: PartyBreakdown, b: PartyBreakdown) => a.totalNetWeight - b.totalNetWeight, align: "right" as const, render: (v: number) => formatNumber(v) },
                      { title: "Slips", dataIndex: "slipCount", key: "slipCount", sorter: (a: PartyBreakdown, b: PartyBreakdown) => a.slipCount - b.slipCount, align: "right" as const },
                      { title: "Vehicles", dataIndex: "uniqueVehicles", key: "uniqueVehicles", align: "right" as const },
                      {
                        title: "Avg Net/Slip",
                        key: "avg",
                        align: "right" as const,
                        render: (_: any, r: PartyBreakdown) => <Text>{formatNumber(Math.round(r.avgNetWeight))} kg</Text>,
                        sorter: (a: PartyBreakdown, b: PartyBreakdown) => a.avgNetWeight - b.avgNetWeight,
                      },
                    ]}
                    dataSource={parties.map((p, i) => ({ ...p, key: String(i) }))}
                    rowKey="key"
                    size="small"
                    pagination={{ pageSize: 10, showTotal: (total) => `${total} parties` }}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default WeightmentComponent;
